import React, { useEffect } from 'react';
import { Box, Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTextInput from '../CustomTextInput';
import CountrySelect from './CountrySelect';
import UAESubdivisionSelect from './UAESubdivisionSelect';
import RegistrationTypeSelect from './RegistrationTypeSelect';
import { CustomerValidation } from '../../validations/CustomerValidation';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../hooks/useRevalidateFormOnLanguageChange';
import { useDispatch, useSelector } from 'react-redux';
import { find, isEmpty, some } from 'lodash';
import { apiClient } from '../../utils/axios';
import { showSnackbar } from '../../api/snackbarSlice';

export default function CustomerPopupForm({ initialData = {}, onClose, onSave }) {
  const { countryCodes } = useSelector((state) => state.countryCodes);
  const { states } = useSelector((state) => state.states);
  const { customers: c, language } = useTranslatedMessages();
  let country = find(countryCodes, { Code: initialData.country_code });
  let state = find(states, { Code: initialData.state });
  const dispatch = useDispatch();

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(CustomerValidation(false, c?.validations?.customer)),
    defaultValues: {
      ...initialData,
      country_code: country ? { key: country.Code, value: country.Country } : '',
      state:
        initialData.country_code === 'MYS'
          ? state
            ? { key: state.Code, value: state.State }
            : ''
          : initialData.state || '',
      registration_type: initialData.registration_type
        ? {
            key: initialData.registration_type,
            value: initialData.registration_type,
          }
        : '',
    },
  });

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const preparePayload = (data) => {
    const payload = {
      ...data,
      customer_type: 'business', // Default to business for popup forms
      country_code: data?.country_code?.key,
      state: data?.state?.key || data?.state,
      registration_type: data?.registration_type?.key,
      tin: data?.tin?.trim() || null,
      email: data.email ? data.email : null,
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === '') {
        delete payload.key;
      }
    });
    delete payload?.participant_id;
    return payload;
  };

  const hasAtLeastOneFieldFilled = (data) => {
    return some(data, (value) => {
      if (typeof value === 'object' && value !== null) {
        return !isEmpty(value?.key) || !isEmpty(value?.value);
      }
      return value !== undefined && value !== null && value !== '';
    });
  };

  const handleProceed = (data) => {
    if (!hasAtLeastOneFieldFilled(data)) {
      dispatch(showSnackbar({ message: 'Please fill atleast one field to proceed', severity: 'error' }));
    } else {
      const customer = preparePayload(data);
      onSave({ ...customer });
      onClose();
    }
  };

  const handleSave = async (data) => {
    const customer = preparePayload(data);
    delete customer._id;
    delete customer.__v;
    await apiClient
      .post('/customers', customer)
      .then((res) => {
        onSave({ ...res.data });
        dispatch(showSnackbar({ message: 'Customer created successfully!', severity: 'success' }));
        onClose();
      })
      .catch((err) => {
        const apiErrors = err?.response?.data?.errors;
        if (apiErrors && typeof apiErrors === 'object')
          Object.entries(apiErrors).forEach(([field, message]) => {
            setError(field, { message: String(message) });
          });
      });
  };

  const handleEdit = async (data) => {
    const customer = preparePayload(data);
    delete customer._id;
    delete customer.__v;
    await apiClient
      .put(`/customers/${customer.profile_id}`, customer)
      .then((res) => {
        onSave({ ...res.data });
        dispatch(showSnackbar({ message: 'Customer updated successfully!', severity: 'success' }));
        onClose();
      })
      .catch((err) => {
        const apiErrors = err?.response?.data?.errors;
        if (apiErrors && typeof apiErrors === 'object')
          Object.entries(apiErrors).forEach(([field, message]) => {
            setError(field, { message: String(message) });
          });
      });
  };
  const selectedCountry = watch('country_code');
  useEffect(() => {
    if (selectedCountry?.key !== 'MYS') {
      if (typeof getValues('state') === 'object') {
        setValue('state', '');
      }
    } else {
      if (typeof getValues('state') === 'string') {
        setValue('state', '');
      }
    }
  }, [selectedCountry, setValue, getValues]);
  return (
    <>
      <Paper elevation={0} sx={{ p: '1rem', overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="name" control={control} errors={errors} label={'Name'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="company_name" control={control} errors={errors} label={'Company Name'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                name="company_name_in_local"
                control={control}
                errors={errors}
                label={'Company Name in Local Language'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="email" control={control} errors={errors} label={'Email'} />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            Address
            <Divider></Divider>
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="address1" control={control} errors={errors} label={'Address Line 1'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="address2" control={control} errors={errors} label={'Address Line 2'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="address3" control={control} errors={errors} label={'Address Line 3'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="city" control={control} errors={errors} label={'City'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CountrySelect name="country_code" control={control} errors={errors} label={'Country'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {selectedCountry?.key === 'MYS' ? (
                <UAESubdivisionSelect errors={errors} control={control} name="state" label={'State'} />
              ) : (
                <CustomTextInput name="state" control={control} errors={errors} label={'State'} />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="postal_code" control={control} errors={errors} label={'Postal Code'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="phone" control={control} errors={errors} label={'Phone'} />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            Business Identifiers
            <Divider></Divider>
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <RegistrationTypeSelect
                errors={errors}
                control={control}
                name="registration_type"
                customerType="business"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                name="registration_number"
                control={control}
                errors={errors}
                label={'Registration Number'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput name="tin" control={control} errors={errors} label={'TIN'} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                name="sst_registration_number"
                control={control}
                errors={errors}
                label={'SST Registration Number'}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {!initialData.profile_id && (
          <Button variant="outlined" onClick={handleSubmit(handleSave)}>
            Add Customer & Proceed
          </Button>
        )}
        {initialData.profile_id && (
          <Button id="update-proceed" variant="outlined" onClick={handleSubmit(handleEdit)}>
            Update & Proceed
          </Button>
        )}
        <Button id="proceed" variant="contained" onClick={() => handleProceed(getValues())}>
          Proceed
        </Button>
      </Box>
    </>
  );
}
